// A first draft of the plan, built once, when the person asks for one.
//
// Each day is solved by the same solver and the same utility the rest of the
// planner uses. An artist with no rating takes the score the genre answers
// seed, and a rating the person has, manual or seeded, replaces it. Goals reach
// the draft only through the tuning the utility already reads.
//
// Nothing here writes anything. The caller turns the result into picks, and from
// then on the plan is the person's and changes only when they change it.

import type { TuningConfig } from './config.ts';
import { solveDay } from './solver.ts';
import type { RestRequirement } from './solver.ts';
import { buildUtilityMap, seedRatings } from './utility.ts';
import type { Artist, DayContext, Id, MustSee, Note, Pick, Profile, Rating } from './types.ts';

export interface DraftInput {
  dayContexts: DayContext[];
  /** The profile with defaults applied, as the solver reads it. */
  profile: Profile;
  ratings: Map<Id, Rating>;
  mustSee: MustSee[];
  artists: Map<Id, Artist>;
  notes?: Map<Id, Note>;
  restByDay: Map<Id, RestRequirement[]>;
  cfg: TuningConfig;
  homeStageId?: Id;
}

export interface DraftPick {
  setId: Id;
  dayId: Id;
  /** Present only when the solve arrives after the set starts or leaves before it ends. */
  startTs?: number;
  endTs?: number;
}

export interface DraftDay {
  dayId: Id;
  setIds: Id[];
  infeasible?: { reason: string };
}

export interface DraftResult {
  setIds: Id[];
  picks: DraftPick[];
  perDay: DraftDay[];
}

export function buildDraft(input: DraftInput): DraftResult {
  const seeded = seedRatings(
    input.profile.userId,
    [...input.artists.values()],
    input.profile.subgenreWeights ?? {},
    input.cfg,
    'draft',
  );
  const ratings = new Map(seeded.map((r) => [r.artistId, r]));
  for (const [artistId, rating] of input.ratings) ratings.set(artistId, rating);

  const lockedArtists = new Set(input.mustSee.filter((m) => m.hardLock).map((m) => m.artistId));
  const picks: DraftPick[] = [];
  const perDay: DraftDay[] = [];

  input.dayContexts.forEach((ctx, dayIndex) => {
    const utility = buildUtilityMap(ctx.sets, {
      profile: input.profile,
      ratings,
      mustSee: input.mustSee,
      artists: input.artists,
      cfg: input.cfg,
      dayIndex,
      notes: input.notes,
    });
    const result = solveDay({
      ctx,
      utility,
      hardLockSetIds: ctx.sets.filter((s) => lockedArtists.has(s.artistId)).map((s) => s.id),
      rest: input.restByDay.get(ctx.day.id) ?? [],
      homeStageId: input.homeStageId,
      cfg: input.cfg,
    });

    if (result.infeasible) {
      perDay.push({ dayId: ctx.day.id, setIds: [], infeasible: { reason: result.infeasible.reason } });
      return;
    }

    const setById = new Map(ctx.sets.map((s) => [s.id, s]));
    const dayIds: Id[] = [];
    for (const block of result.blocks) {
      if (!block.setId || block.mode === 'travel' || dayIds.includes(block.setId)) continue;
      const set = setById.get(block.setId);
      if (!set) continue;
      dayIds.push(set.id);
      picks.push({
        setId: set.id,
        dayId: ctx.day.id,
        startTs: block.startTs > set.startTs ? block.startTs : undefined,
        endTs: block.endTs < set.endTs ? block.endTs : undefined,
      });
    }
    perDay.push({ dayId: ctx.day.id, setIds: dayIds });
  });

  return { setIds: picks.map((p) => p.setId), picks, perDay };
}

// Returns the whole picks table after the draft is written, or null when this
// person already has picks and replacing them was not asked for. Other people's
// picks are carried through unchanged.
export function draftPicks(existing: Pick[], userId: Id, draft: DraftResult, replace: boolean, now: string): Pick[] | null {
  const mine = existing.filter((p) => p.userId === userId);
  if (mine.length > 0 && !replace) return null;
  return [
    ...existing.filter((p) => p.userId !== userId),
    ...draft.picks.map((p) => ({ userId, addedAt: now, ...p })),
  ];
}
